import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Gift, Sparkles, Check, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DISCOUNT_CODE = "PROOFIX25";
const POPUP_SHOWN_KEY = "proofix_discount_popup_shown";
const DISCOUNT_CLAIMED_KEY = "proofix_discount_claimed";

export default function PartnerDiscountPopup({ referralCode, affiliateName, onClose }) {
  const [isOpen, setIsOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [showReminder, setShowReminder] = useState(false);

  useEffect(() => {
    // Check if popup was already shown and discount claimed
    const discountClaimed = localStorage.getItem(DISCOUNT_CLAIMED_KEY);
    const popupShown = sessionStorage.getItem(POPUP_SHOWN_KEY);
    
    if (referralCode && !discountClaimed) {
      // Small delay for better UX
      const timer = setTimeout(() => {
        setIsOpen(true);
        sessionStorage.setItem(POPUP_SHOWN_KEY, "true");
      }, 1500);
      
      return () => clearTimeout(timer);
    } else if (referralCode && discountClaimed) {
      // Discount already claimed, no popup needed
      setIsOpen(false);
    }
  }, [referralCode]);

  const handleClose = () => {
    setIsOpen(false);
    // Show reminder bar if they closed without claiming
    if (!isSuccess) {
      setShowReminder(true);
    }
    if (onClose) onClose();
  };

  const handleReminderClick = () => {
    setShowReminder(false);
    setIsOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email.trim()) {
      toast.error("Vul je e-mailadres in");
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error("Vul een geldig e-mailadres in");
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/referral/claim-discount`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.trim(),
          referral_code: referralCode,
          discount_code: DISCOUNT_CODE
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setIsSuccess(true);
        localStorage.setItem(DISCOUNT_CLAIMED_KEY, "true");
        localStorage.setItem("proofix_discount_email", email.trim());
        toast.success("Kortingscode verzonden naar je inbox!");
      } else {
        toast.error(data.detail || "Er ging iets mis");
      }
    } catch (error) {
      console.error("Error claiming discount:", error);
      toast.error("Er ging iets mis. Probeer het opnieuw.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {/* Main Popup */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
              onClick={handleClose}
            />
            
            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
            >
              <div 
                className="relative w-full max-w-md bg-slate-900 rounded-2xl border border-slate-800 shadow-2xl overflow-hidden"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Close button */}
                <button
                  onClick={handleClose}
                  className="absolute top-4 right-4 p-2 text-slate-400 hover:text-white transition-colors z-10"
                  data-testid="close-discount-popup"
                >
                  <X className="w-5 h-5" />
                </button>

                {!isSuccess ? (
                  <>
                    {/* Header with gradient */}
                    <div className="bg-gradient-to-br from-blue-500/20 via-purple-500/10 to-slate-900 p-8 pb-6">
                      <div className="flex items-center justify-center mb-4">
                        <div className="relative">
                          <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center">
                            <Gift className="w-8 h-8 text-white" />
                          </div>
                          <div className="absolute -top-1 -right-1 w-6 h-6 bg-amber-400 rounded-full flex items-center justify-center">
                            <Sparkles className="w-3 h-3 text-amber-900" />
                          </div>
                        </div>
                      </div>
                      
                      <h2 className="text-2xl font-bold text-white text-center mb-2">
                        Je bent uitgenodigd via een Proofix partner 🎉
                      </h2>
                      {affiliateName && (
                        <p className="text-blue-400 text-center text-sm">
                          Uitgenodigd door: {affiliateName}
                        </p>
                      )}
                    </div>

                    {/* Content */}
                    <div className="p-8 pt-6">
                      <p className="text-slate-300 text-center mb-6">
                        Laat je e-mailadres achter en ontvang{" "}
                        <span className="text-white font-bold">25% korting</span>{" "}
                        op je DBA analyse.
                      </p>

                      {/* Hidden discount code teaser */}
                      <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 mb-6 text-center relative overflow-hidden">
                        <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Jouw kortingscode</p>
                        <div className="relative">
                          <p className="text-2xl font-mono font-bold text-slate-600 blur-sm select-none">XXXXXXXX</p>
                          <div className="absolute inset-0 flex items-center justify-center">
                            <span className="text-sm text-blue-400 bg-slate-800/90 px-3 py-1 rounded-full">
                              Vul je email in om te onthullen
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Form */}
                      <form onSubmit={handleSubmit} className="space-y-4">
                        <Input
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="Vul je e-mailadres in"
                          className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-blue-500 h-12"
                          data-testid="discount-email-input"
                        />
                        
                        <Button
                          type="submit"
                          disabled={isLoading}
                          className="w-full h-12 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-semibold rounded-xl"
                          data-testid="claim-discount-btn"
                        >
                          {isLoading ? (
                            <>
                              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                              Bezig...
                            </>
                          ) : (
                            <>
                              <Gift className="w-5 h-5 mr-2" />
                              Ontvang mijn kortingscode
                            </>
                          )}
                        </Button>
                      </form>

                      {/* Privacy text */}
                      <p className="text-xs text-slate-500 text-center mt-4">
                        We gebruiken je e-mailadres om je kortingscode en eventuele rapportinformatie te sturen. Geen spam.
                      </p>

                      {/* Exclusive badge */}
                      <div className="flex items-center justify-center gap-2 mt-6 text-slate-400 text-sm">
                        <Sparkles className="w-4 h-4 text-amber-400" />
                        <span>Exclusief beschikbaar via Proofix partners</span>
                      </div>
                    </div>
                  </>
                ) : (
                  /* Success state */
                  <div className="p-8 text-center">
                    <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                      <Check className="w-8 h-8 text-emerald-400" />
                    </div>
                    
                    <h2 className="text-2xl font-bold text-white mb-2">
                      Gelukt! 🎉
                    </h2>
                    <p className="text-slate-300 mb-6">
                      Je kortingscode is verzonden naar <span className="text-white font-medium">{email}</span>
                    </p>

                    <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 mb-6">
                      <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Jouw kortingscode</p>
                      <p className="text-3xl font-mono font-bold text-blue-400">{DISCOUNT_CODE}</p>
                      <p className="text-emerald-400 text-sm mt-2">25% korting op je DBA analyse</p>
                    </div>

                    <Button
                      onClick={handleClose}
                      className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-semibold rounded-xl"
                    >
                      Start de DBA Check
                    </Button>
                  </div>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Sticky Reminder Bar */}
      <AnimatePresence>
        {showReminder && !isOpen && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:w-auto z-40"
          >
            <div className="bg-slate-900 border border-slate-700 rounded-xl p-4 shadow-2xl flex items-center gap-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-lg flex items-center justify-center">
                  <Gift className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-white font-medium text-sm">25% partnerkorting beschikbaar</p>
                  <p className="text-slate-400 text-xs">Code: {DISCOUNT_CODE}</p>
                </div>
              </div>
              <Button
                onClick={handleReminderClick}
                size="sm"
                className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white text-sm whitespace-nowrap"
              >
                Claim korting
              </Button>
              <button
                onClick={() => setShowReminder(false)}
                className="p-1 text-slate-500 hover:text-slate-300"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

// Export utility functions
export function hasClaimedDiscount() {
  return localStorage.getItem(DISCOUNT_CLAIMED_KEY) === "true";
}

export function getDiscountCode() {
  if (hasClaimedDiscount()) {
    return DISCOUNT_CODE;
  }
  return null;
}

export function getDiscountEmail() {
  return localStorage.getItem("proofix_discount_email");
}
